import { ReactNode, useState } from "react";
import { SocialIcon } from "../utils/social-icon";
import { ItemWithMetaData, RelevantEntity } from "../api/Items";
import Badge from "../shared-components/Badge";
import { XIcon } from "../shared-components/icons";
import LeftBorderedContainer from "../shared-components/LeftBorderedContainer";
import ReactTooltip from "react-tooltip";
import { formatProfileAddedByLabel } from "./util";

type ProfileRowProps = {
  item: ItemWithMetaData;
  tabColor?: string;
  onDelete?: (itemUUID: string) => void;
  className?: string;
  children?: ReactNode;
  isManualAdd?: boolean;
  isReadOnly?: boolean;
};

export default function ProfileRow({
  item,
  tabColor,
  onDelete,
  className,
  children,
  isManualAdd,
  isReadOnly,
}: ProfileRowProps) {
  const [selectedEntity, setSelectedEntity] = useState<{
    entityField: string;
    entities: { score: string; entities: string }[];
  }>();
  const profileUrl = item.item.content.url || "";

  const handleRemoveProfile = () => {
    onDelete?.(item.item.external_id);
  };

  return (
    <>
      <div className="bg-white mx-6 my-3">
        <LeftBorderedContainer
          color={tabColor}
          className={`${className} border-y-gray-300 border-r-gray-300`}
        >
          <div className="flex flex-1 items-center overflow-hidden">
            <div>
              <SocialIcon url={profileUrl} />
            </div>
            <div className="flex-1 truncate pl-2">
              <a
                href={profileUrl}
                target="_blank"
                rel="noreferrer"
                className="text-base font-normal leading-6 truncate"
              >
                {profileUrl}
              </a>
              {!isManualAdd && (
                <MatchingEntities item={item} setSelected={setSelectedEntity} />
              )}
              <AddedByNote item={item} />
            </div>
            {!isReadOnly && (
              <div className="flex px-5">
                <button onClick={handleRemoveProfile} className="">
                  <XIcon />
                </button>
              </div>
            )}
          </div>

          {selectedEntity?.entityField && (
            <ReactTooltip
              id={selectedEntity.entityField}
              place="top"
              type="light"
              effect="solid"
              backgroundColor="white"
            >
              <div>
                {selectedEntity.entities?.map((e, i) => (
                  <div key={i}>{`${e.score} of ${e.entities}`}</div>
                ))}
              </div>
            </ReactTooltip>
          )}
        </LeftBorderedContainer>
      </div>
    </>
  );
}

type MatchingEntitiesProps = { item: ItemWithMetaData; setSelected?: any };

export const MatchingEntities = ({
  item,
  setSelected,
}: MatchingEntitiesProps) => {
  const matchingEntities: RelevantEntity[] = item.entities;
  const entitiesMap: Map<string, RelevantEntity[]> = new Map();
  matchingEntities.forEach((entity: RelevantEntity) => {
    let current = entitiesMap.get(entity.field) || [];
    current.concat(entity);
    entitiesMap.set(entity.field, current);
  });

  return (
    <div className="flex flex-wrap m-1">
      {entitiesMap &&
        Array.from(entitiesMap.keys()).map((entityField: string) => {
          const elements = matchingEntities
            .filter((entity: RelevantEntity) => entity.field === entityField)
            .map((entity: RelevantEntity) => ({
              score: JSON.parse(entity.value).score,
              entities: JSON.parse(entity.value).entities,
            }));
          if (elements.reduce((a, b) => a + b.score, 0) === 0) {
            return null;
          }
          return (
            <div
              key={entityField}
              className="capitalize"
              data-tooltip
              data-for={entityField}
              onMouseEnter={() => {
                setSelected?.({ entityField, entities: elements });
              }}
              onMouseLeave={() => {
                // setSelected({});
              }}
            >
              <Badge
                color="gray"
                hasDot={false}
                className={"font-medium text-xs mr-2"}
              >
                {entityField}
              </Badge>
            </div>
          );
        })}
    </div>
  );
};

type AddedByNoteProps = { item: ItemWithMetaData };

const AddedByNote = ({ item }: AddedByNoteProps) => {
  const label = formatProfileAddedByLabel(item.item.content?.added_by);
  if (label) {
    return (
      <div className="border-black text-xs leading-5 font-normal">
        Added by <span className="font-semibold">{label}</span>
      </div>
    );
  }
  return <div className="border-black text-xs leading-5 font-normal"></div>;
};
